import { Component, inject, input, Input, OnDestroy, output, viewChild, ChangeDetectionStrategy } from "@angular/core";
import { AbstractControl, ReactiveFormsModule, UntypedFormGroup, ValidationErrors } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DescriptionEditorComponent } from "common/editor/description-editor.component";
import { DurationInput } from "common/form/input/types/duration-input";
import { InputBase } from "common/form/input/types/input-base";
import { RichTextInput } from "common/form/input/types/rich-text-input";
import { SelectionInput, SelectionOption } from "common/form/input/types/selection-input";
import { FloatInput } from "common/form/input/types/float-input";
import { NgClass } from "@angular/common";
import { TrimDirective } from "common/trim.directive";
import { AutofocusDirective } from "common/autofocus/autofocus.directive";
import { AutoGrowDirective } from "common/autoGrow/autogrow.directive";
import { ValidateOnBlurDirective } from "common/form/validate-on-blur.directive";
import { ColorPickerComponent } from "common/color-picker/color-picker.component";
import { DateTimePickerComponent } from "common/date-time-picker/date-time-picker.component";
import { OrderAlphabeticallyPipe } from "common/sort/order-alphabetically.pipe";

@Component({
	selector: "stages-input",
	templateUrl: "./input.component.html",
	styleUrls: ["input.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		NgClass,
		ReactiveFormsModule,
		TrimDirective,
		AutofocusDirective,
		TranslateModule,
		AutoGrowDirective,
		ValidateOnBlurDirective,
		DescriptionEditorComponent,
		ColorPickerComponent,
		DateTimePickerComponent,
		OrderAlphabeticallyPipe,
	],
})
export class InputComponent implements OnDestroy {
	private readonly translate = inject(TranslateService);

	readonly form = input.required<UntypedFormGroup>();
	// TODO: Skipped for migration because:
	//  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
	//  and migrating would break narrowing currently.
	@Input({ required: true }) input!: InputBase<unknown>;
	readonly disabled = input<boolean>(false);
	readonly additionalValidationErrors = input<ValidationErrors | null>();
	readonly changedControlKey = output<string>();
	readonly hideLabel = input<boolean>(false);
	readonly valueChanged = output<unknown>();

	readonly descriptionComp = viewChild<DescriptionEditorComponent>("descriptionComp");

	ngOnDestroy(): void {
		const descriptionComp = this.descriptionComp();
		if (descriptionComp) {
			descriptionComp.quit();
		}
	}

	isValid(): boolean {
		return this.formControl.valid;
	}

	get isHidden(): boolean {
		return this.input.hidden;
	}

	get formControl(): AbstractControl {
		const control = this.form().controls[this.input.key];
		if (!control) {
			throw new Error(`Could not get control '${this.input.key}' from formGroup.`);
		}

		return control;
	}

	getErrors(): string[] {
		const errors = this.formControl.errors;
		const keys: string[] = [];
		if (errors) {
			Object.keys(errors).forEach((key: string) => keys.push(key));
		}

		const additionalValidationErrors = this.additionalValidationErrors();
		if (additionalValidationErrors) {
			Object.keys(additionalValidationErrors).forEach((key: string) => keys.push(key));
		}

		return keys;
	}

	hasErrors(formControl: AbstractControl): boolean {
		return !!formControl.errors || !!this.additionalValidationErrors();
	}

	getInputLength(): number {
		const value = this.formControl.value;
		return value && typeof (value === "string") ? value.length : 0;
	}

	toggleCheckbox(): void {
		if (!this.input.disabled) {
			const value = !this.formControl.value;
			this.input.setValue(value);
			this.formControl.setValue(value);
			this.changedControlKey.emit(this.input.key);
		}
	}

	selectRadioChoice(selectedOption: string): void {
		if (!this.input.disabled) {
			this.input.setValue(selectedOption);
			this.formControl.setValue(selectedOption);
			this.changedControlKey.emit(this.input.key);
		}
	}

	get fieldLabel(): string {
		return this.getLabelName(this.input.translateKey, this.input.untranslatableName, this.input.key);
	}

	getOptionLabel = (option: SelectionOption): string => {
		return this.getLabelName(option.translateKey, option.untranslatableName, option.value);
	};

	private getLabelName(translateKey: Maybe<string>, untranslatableName: Maybe<string>, fallback: string): string {
		if (translateKey) {
			return this.translate.instant(translateKey);
		}
		if (untranslatableName) {
			return untranslatableName;
		}
		return fallback;
	}

	get selectionOptions(): SelectionOption[] | null {
		return this.input instanceof DurationInput
			? this.input.selectionOptions
			: (this.input as SelectionInput).selectionOptions;
	}

	isMultiSelect(): boolean {
		return (this.input as SelectionInput).multiple;
	}

	get size(): number | undefined {
		return (this.input as SelectionInput).size;
	}

	get min(): number | undefined {
		return (this.input as FloatInput).min;
	}

	get max(): number | undefined {
		return (this.input as FloatInput).max;
	}

	isAlphabeticallyOrderedSelection(): boolean {
		return (this.input as SelectionInput).alphabetical;
	}

	get richtextInput(): RichTextInput {
		return this.input as RichTextInput;
	}

	nop(): void {}

	onValueChange(input: InputBase<unknown>): void {
		this.changedControlKey.emit(input.key);
		this.valueChanged.emit(this.formControl.value);
	}
}
